import React, { MouseEvent, useState } from "react";
import styled from "styled-components";
import { position, PositionProps, layout, LayoutProps } from "styled-system";
import {
  useOverlay,
  usePreventScroll,
  DismissButton,
} from "@react-aria/overlays";
import { FocusScope } from "@react-aria/focus";
import css from "@styled-system/css";

import Button from "../Button/Button";
import Icon from "../Icon/Icon";
import logo from "./cag-logo.png";
import Link from "../Link/Link";
import Typography from "../Typography/Typography";
import { processEventLabel } from "../Navigation/navItem.functions";

import { NavMenuSection } from "../BCAHeader/bcaHeader.type";
import { MenuLink } from "../Navigation/MenuLink.type";

const LogoImg = styled.img<{ height?: LayoutProps["height"] }>`
  ${layout}
`;

const MobileMenuBackdrop = styled.div<{ itemsColor: string }>`
  position: fixed;
  height: 100vh;
  width: 100vw;
  top: 0;
  right: 0;
  overflow: none;
  z-index: 99;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  ${(props) => css({ color: props.itemsColor })}
`;

const LinkLi = styled.li`
  line-height: 2.5;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CloseButton = styled.button<PositionProps>`
  cursor: pointer;
  background: transparent;
  border: none;
  padding: 0;
  ${position}
`;

const Hr = styled.hr`
  width: 100%;
  height: 1px;
  background: #dedede;
  border: none;
  margin-top: 10px;
  margin-bottom: 30px;
`;

const renderFirstLevel = (
  navItems: MenuLink[],
  selectSubsection: (index: number) => void
) => {
  return (
    <ul style={{ listStyle: "none", padding: "0", marginTop: "0" }}>
      {navItems.map((item, index) => {
        const eventLabel = processEventLabel(item.label);
        const eventAction = item.label === "Search" ? "search" : "navigate";

        return (
          <LinkLi key={item.id}>
            <Link
              href={item.link}
              data-en="click"
              data-ec="button"
              data-el={eventLabel}
              data-ea={eventAction}
              data-es="header"
            >
              {item.label}
            </Link>
            {item.children || item.sections ? (
              <Button
                variant="tertiary"
                icon="angle-right"
                iconAlignment="right"
                iconColor="text-color-hover"
                iconWeight="regular"
                iconHeight="20px"
                iconWidth="14px"
                paddingX={0}
                onClick={(e: MouseEvent) => {
                  e.stopPropagation();
                  selectSubsection(index);
                }}
              />
            ) : null}
          </LinkLi>
        );
      })}
    </ul>
  );
};

const renderSubSections = (section: MenuLink) => {
  const eventAction = section.label === "Search" ? "search" : "navigate";
  const eventSection = processEventLabel(section.label);

  const renderSection = (subSection: NavMenuSection, index: number) => {
    return (
      <div key={subSection.id}>
        {index > 0 && <Hr></Hr>}
        <ul style={{ listStyle: "none", padding: "0", marginTop: "0" }}>
          {subSection.children.map((item: MenuLink, index: number) => {
            const eventLabel = processEventLabel(item.label);

            if (index === 0) {
              return (
                <LinkLi key={item.id}>
                  <Typography
                    key={item.id}
                    variant="body"
                    color="lighter-text-color"
                    css={`
                      font-weight: 500;
                      margin-bottom: 8px;
                      text-transform: uppercase;
                    `}
                  >
                    {item.label}
                  </Typography>
                </LinkLi>
              );
            }
            return (
              <LinkLi key={item.id}>
                <Link
                  href={item.link}
                  data-en="click"
                  data-ec="button"
                  data-el={eventLabel}
                  data-ea={eventAction}
                  data-es={eventSection}
                >
                  {item.label}
                </Link>
                {item.children || item.sections ? (
                  <Icon
                    name="angle-right"
                    weight="regular"
                    color="#3f403f"
                    width="14px"
                    height="20px"
                  ></Icon>
                ) : null}
              </LinkLi>
            );
          })}
        </ul>
      </div>
    );
  };
  if (section.sections) {
    return section.sections.map((subSection, index) =>
      renderSection(subSection, index)
    );
  }
  return (
    <ul style={{ listStyle: "none", padding: "0", marginTop: "0" }}>
      {section.children &&
        section.children.map((item: MenuLink) => {
          const eventLabel = processEventLabel(item.label);

          return (
            <LinkLi key={item.id}>
              <Link
                href={item.link}
                data-en="click"
                data-ec="button"
                data-el={eventLabel}
                data-ea={eventAction}
                data-es={eventSection}
              >
                {item.label}
              </Link>
            </LinkLi>
          );
        })}
    </ul>
  );
};

const MobileMenu = React.forwardRef<HTMLDivElement, any>(
  (
    { navItems, onClose, isOpen, customLogo, itemsColor, ...otherProps },
    ref
  ) => {
    const [selectedSectionIndex, setSelectionIndex] = useState<number | null>(
      null
    );
    usePreventScroll();

    let { overlayProps } = useOverlay(
      {
        onClose,
        isOpen,
        isDismissable: true,
      },
      // @ts-ignore
      ref
    );

    const selectSubsection = (index: number) => {
      if (navItems[index].sections || navItems[index].children) {
        setSelectionIndex(index);
      }
    };
    return (
      <MobileMenuBackdrop itemsColor={itemsColor}>
        <FocusScope contain restoreFocus>
          <div
            style={{
              background: "white",
              width: "325px",
              height: "100%",
              marginLeft: "auto",
              padding: "16px 32px",
              boxSizing: "border-box",
              position: "relative",
            }}
            {...overlayProps}
            {...otherProps}
            ref={ref}
          >
            {selectedSectionIndex !== null ? (
              <Button
                text={`Back to ${navItems[selectedSectionIndex].label}`}
                variant="tertiary"
                icon="arrow-right-solid"
                iconAlignment="left"
                css={`
                  padding: 0;
                  margin-left: -16px;
                  margin-top: -8px;
                  svg {
                    transform: rotate(180deg);
                  }
                  cursor: pointer;
                `}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectionIndex(null);
                }}
              />
            ) : (
              customLogo || (
                <LogoImg
                  height="32px"
                  src={logo}
                  alt="constellation automotive logo"
                  aria-hidden="true"
                />
              )
            )}

            <CloseButton
              position="absolute"
              right="27px"
              top="20px"
              onClick={onClose}
            >
              <Icon name="times" weight="light" width="16px" color="#3f403f" />
            </CloseButton>
            <nav
              style={{
                height: window.innerHeight - 100,
                marginTop: "20px",
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                justifyContent: "start",
              }}
            >
              {selectedSectionIndex !== null
                ? renderSubSections(navItems[selectedSectionIndex])
                : renderFirstLevel(navItems, selectSubsection)}
            </nav>
            <DismissButton onDismiss={onClose} />
          </div>
        </FocusScope>
      </MobileMenuBackdrop>
    );
  }
);

export default MobileMenu;
